import React, { useState, useEffect, useRef } from 'react';
import { FiLoader, FiCheckCircle, FiAlertCircle, FiRefreshCw, FiMinimize2, FiMaximize2 } from 'react-icons/fi';
import Modal from './Modal';
import { crosswalkApi } from '../services/api';

const STATUS_COLORS = {
  queued: 'text-blue-600 bg-blue-50',
  running: 'text-amber-600 bg-amber-50',
  processing: 'text-amber-600 bg-amber-50',
  polling: 'text-amber-600 bg-amber-50',
  enriching: 'text-purple-600 bg-purple-50',
  saving: 'text-indigo-600 bg-indigo-50',
  completed: 'text-green-600 bg-green-50',
  failed: 'text-red-600 bg-red-50',
  no_proposals: 'text-slate-600 bg-slate-50',
};

const CrosswalkModal = ({ isOpen, minimized, onClose, onMinimize, onRestore, onComplete, crosswalkJobs = [] }) => {
  const [jobs, setJobs] = useState([]);
  const [polling, setPolling] = useState(false);
  const intervalRef = useRef(null);

  // Initialize jobs from props
  useEffect(() => {
    if (isOpen && crosswalkJobs.length > 0) {
      setJobs(crosswalkJobs.map(j => ({ ...j, details: null, loading: false })));
      setPolling(true);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isOpen, crosswalkJobs]);

  // Poll for status updates
  useEffect(() => {
    if (!polling || jobs.length === 0) return;

    const terminalStatuses = ['completed', 'failed', 'no_proposals'];
    
    const poll = async () => {
      const pendingJobs = jobs.filter(j => j.jobId && !terminalStatuses.includes(j.status));
      if (pendingJobs.length === 0) {
        setPolling(false);
        return;
      }

      // One round-trip for all pending jobs instead of N single-job calls.
      const detailsById = {};
      try {
        const batch = await crosswalkApi.getJobStatusBatch(pendingJobs.map(j => j.jobId));
        (batch || []).forEach(d => { if (d?.jobId) detailsById[d.jobId] = d; });
      } catch {
        return; // transient error — retry on the next tick
      }

      const updatedJobs = jobs.map((job) => {
        if (!job.jobId || terminalStatuses.includes(job.status)) return job;
        const details = detailsById[job.jobId];
        if (!details) return job;
        return { ...job, status: details.status || job.status, phase: details.phase, details };
      });
      setJobs(updatedJobs);
    };

    poll();
    intervalRef.current = setInterval(poll, 3000);
    return () => clearInterval(intervalRef.current);
  }, [polling, jobs.length]);

  const terminalStatuses = ['completed', 'failed', 'no_proposals'];
  const allDone = jobs.length > 0 && jobs.every(j => terminalStatuses.includes(j.status));

  const handleClose = () => {
    const isDone = allDone;
    setPolling(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    onClose(isDone);
  };

  const handleMinimize = () => onMinimize();
  const handleRestore = () => onRestore();

  const completedCount = jobs.filter(j => terminalStatuses.includes(j.status)).length;
  const successCount = jobs.filter(j => j.status === 'completed').length;
  const failedCount = jobs.filter(j => j.status === 'failed').length;
  const noProposalsCount = jobs.filter(j => j.status === 'no_proposals').length;

  const getStatusIcon = (status) => {
    if (status === 'queued') return <FiRefreshCw className="text-xs" />;
    if (['running', 'processing', 'polling', 'enriching', 'saving'].includes(status)) {
      return <FiLoader className="animate-spin text-xs" />;
    }
    if (status === 'completed') return <FiCheckCircle className="text-xs" />;
    if (status === 'failed') return <FiAlertCircle className="text-xs" />;
    if (status === 'no_proposals') return <FiCheckCircle className="text-xs" />;
    return null;
  };

  const getStatusLabel = (job) => {
    if (job.phase && job.status === 'running') {
      return `${job.status} (${job.phase})`;
    }
    return job.status;
  };

  useEffect(() => {
    if (allDone && onComplete) onComplete();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);

  if (!isOpen) return null;
  const progressText = allDone
    ? `Complete: ${successCount} succeeded${failedCount > 0 ? `, ${failedCount} failed` : ''}${noProposalsCount > 0 ? `, ${noProposalsCount} no proposals` : ''}`
    : `Processing ${completedCount}/${jobs.length}...`;

  // Minimized floating widget
  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
        <div
          className="bg-white rounded-xl shadow-lg border border-slate-200 w-80 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={handleRestore}
        >
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2.5 min-w-0">
              {!allDone ? (
                <FiLoader className="animate-spin text-blue-500 flex-shrink-0" />
              ) : (
                <FiCheckCircle className="text-green-500 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">Crosswalk Processing</p>
                <p className="text-xs text-slate-500 truncate">{progressText}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); handleRestore(); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                title="Expand"
              >
                <FiMaximize2 className="text-sm" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>
          {/* Progress bar */}
          {!allDone && (
            <div className="px-4 pb-3">
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${jobs.length > 0 ? (completedCount / jobs.length) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full modal
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Crosswalk Processing"
      size="md"
      footer={
        <button
          onClick={handleMinimize}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5"
        >
          <FiMinimize2 className="text-sm" />
          Minimize
        </button>
      }
    >
      <div className="space-y-3">
        {/* Summary */}
        <div className="flex items-center gap-2 text-sm text-slate-600">
          {!allDone ? (
            <>
              <FiLoader className="animate-spin text-blue-500" />
              <span>Processing crosswalk for {jobs.length} questionnaire(s)...</span>
            </>
          ) : (
            <>
              <FiCheckCircle className="text-green-500" />
              <span>
                Crosswalk complete: {successCount} succeeded
                {failedCount > 0 && `, ${failedCount} failed`}
                {noProposalsCount > 0 && `, ${noProposalsCount} no proposals`}
              </span>
            </>
          )}
        </div>

        {/* Job list */}
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {jobs.map((job, index) => {
            const validateContext = job.details?.result?.validateContext;
            const questionnaireName = validateContext?.questionnaireName;
            const assessmentDescription = validateContext?.assessmentDescription;
            const proposalCount = job.details?.result?.proposals?.summary?.totalProposals || 0;
            const sourceCount = job.details?.result?.loadInputs?.sourceAnswers?.count || 0;
            const targetCount = job.details?.result?.loadInputs?.unanswered?.count || 0;
            const hasSourceData = sourceCount > 0 && targetCount > 0;
            
            return (
              <div key={index} className="flex items-center justify-between px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    Assessment #{job.assessmentId} — {questionnaireName || `Questionnaire #${job.questionnaireId}`}
                  </p>
                  {assessmentDescription && (
                    <p className="text-xs text-slate-500 truncate mt-0.5">{assessmentDescription}</p>
                  )}
                  {job.jobId && (
                    <p className="text-xs text-slate-400 truncate mt-0.5">{job.jobId}</p>
                  )}
                  {job.details?.result?.proposals?.summary && (
                    proposalCount > 0 ? (
                      <p className="text-xs text-green-600 mt-0.5">
                        {proposalCount} proposal(s) generated
                      </p>
                    ) : hasSourceData ? (
                      <p className="text-xs text-amber-600 mt-0.5">
                        Processing {targetCount} questions with {sourceCount} source answers (local matching)
                      </p>
                    ) : (
                      <p className="text-xs text-slate-500 mt-0.5">
                        0 proposal(s) generated
                      </p>
                    )
                  )}
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[job.status] || 'text-slate-600 bg-slate-50'}`}>
                  {getStatusIcon(job.status)}
                  {getStatusLabel(job)}
                </span>
              </div>
            );
          })}
        </div>

        {jobs.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4">
            No crosswalk jobs were triggered. Assessments may not have linked questionnaires.
          </p>
        )}
      </div>
    </Modal>
  );
};

export default CrosswalkModal;
